/**
 * localStorage Utilities
 * Core localStorage operations for loads, pagination, and active tab
 * Filter serialization and tab derivation are split into separate modules
 */

import { CanonicalLoad } from '@numeo/types-core'
import { readPerWindow, windowScopedKey, writePerWindow } from './perWindowStorage'

/** Storage key constants */
const STORAGE_KEY_PREFIX = 'private_loads'
export const STORAGE_KEYS = {
    ACTIVE_TAB: `${STORAGE_KEY_PREFIX}_active_tab`,
    FILTERS: `${STORAGE_KEY_PREFIX}_filters`,
    LOADS: `${STORAGE_KEY_PREFIX}_loads`,
    PAGINATION: `${STORAGE_KEY_PREFIX}_pagination`,
    TAB_CREATED: `${STORAGE_KEY_PREFIX}_tab_created`,
} as const

const TAB_TTL_MS = 24 * 60 * 60 * 1000 // 1 day

// Per-window persistence lives in ./perWindowStorage (single implementation, shared with Box Truck). Filters /
// active tab / pagination are namespaced by windowId so two monitors showing the same search stay independent;
// see ./windowId.ts. Load Hub's only extra need is telling useStorageSync which keys are "ours":

/** Base keys persisted per window. Loads are excluded (never persisted); tab-created timestamps stay shared
 *  (keyed by tabId, not window). */
const WINDOW_SCOPED_KEYS = [STORAGE_KEYS.ACTIVE_TAB, STORAGE_KEYS.FILTERS, STORAGE_KEYS.PAGINATION]

/** The namespaced keys the CURRENT window reacts to (see useStorageSync). Because these are unique to this
 *  window, a native cross-window `storage` event (another window's keys, or the shared mirror) never
 *  matches — which is exactly what keeps two windows independent. */
export const getWindowScopedSyncKeys = (): string[] => WINDOW_SCOPED_KEYS.map(windowScopedKey)

/** Save tab creation timestamp. `createdKey` lets a second tab surface (e.g. Box Truck) reuse this same
 *  24h-TTL mechanism against its own storage key instead of re-implementing it. */
export const saveTabCreatedAt = (tabId: string, createdKey: string = STORAGE_KEYS.TAB_CREATED) => {
    safeStorageOperation(
        () => {
            const stored = localStorage.getItem(createdKey)
            const map: Record<string, number> = stored ? JSON.parse(stored) : {}
            map[tabId] = Date.now()
            localStorage.setItem(createdKey, JSON.stringify(map))
        },
        undefined,
        'saveTabCreatedAt'
    )
}

/** Remove tab creation timestamp (see `saveTabCreatedAt` for `createdKey`). */
export const removeTabCreatedAt = (tabId: string, createdKey: string = STORAGE_KEYS.TAB_CREATED) => {
    safeStorageOperation(
        () => {
            const stored = localStorage.getItem(createdKey)
            if (!stored) return
            const map: Record<string, number> = JSON.parse(stored)
            delete map[tabId]
            localStorage.setItem(createdKey, JSON.stringify(map))
        },
        undefined,
        'removeTabCreatedAt'
    )
}

/**
 * Get IDs of tabs that have expired.
 * A tab is considered expired when:
 *   - its timestamp is older than `ttlMs` (24h by default), OR
 *   - `knownTabIds` is provided and the tab has no timestamp at all
 *     (missing entry → treat as TTL reached so orphaned tabs get purged on reload).
 * `createdKey` selects the timestamp store (see `saveTabCreatedAt`).
 * `ttlMs` allows callers to choose a longer tab lifetime without changing Load Hub's default.
 */
export const getExpiredTabIds = (knownTabIds?: string[], createdKey: string = STORAGE_KEYS.TAB_CREATED, ttlMs = TAB_TTL_MS): string[] => {
    return safeStorageOperation(
        () => {
            const stored = localStorage.getItem(createdKey)
            const map: Record<string, number> = stored ? JSON.parse(stored) : {}
            const now = Date.now()

            const expired = new Set(
                Object.entries(map)
                    .filter(([, createdAt]) => now - createdAt > ttlMs)
                    .map(([id]) => id)
            )

            if (knownTabIds) {
                for (const id of knownTabIds) {
                    if (!(id in map)) expired.add(id)
                }
            }

            return Array.from(expired)
        },
        [],
        'getExpiredTabIds'
    )
}

/**
 * Safe localStorage operations with error handling
 * Prevents crashes on quota errors and other storage issues
 */
export const safeStorageOperation = <T>(operation: () => T, fallback: T, context: string): T => {
    try {
        return operation()
    } catch (error) {
        console.error(`Error in localStorage operation (${context}):`, error)
        return fallback
    }
}

/** Load loads from localStorage */
export const loadLoadsFromLocalStorage = (): Record<string, CanonicalLoad[]> | null => {
    return safeStorageOperation(
        () => {
            const stored = localStorage.getItem(STORAGE_KEYS.LOADS)
            return stored ? JSON.parse(stored) : null
        },
        null,
        'loadLoadsFromLocalStorage'
    )
}

/** Pagination state type */
type PaginationState = { offset: number; hasMore: boolean; isLoadingMore: boolean; total?: number }

/** Save pagination state (per window) */
export const savePaginationToLocalStorage = (tabPagination: Record<string, PaginationState>) => {
    writePerWindow(STORAGE_KEYS.PAGINATION, JSON.stringify(tabPagination))
}

/** Load pagination state (this window's, seeded from the last-saved state on a fresh window) */
export const loadPaginationFromLocalStorage = (): Record<string, PaginationState> | null => {
    return safeStorageOperation(
        () => {
            const stored = readPerWindow(STORAGE_KEYS.PAGINATION)
            return stored ? JSON.parse(stored) : null
        },
        null,
        'loadPaginationFromLocalStorage'
    )
}

/** Save active tab ID (per window) */
export const saveActiveTabToLocalStorage = (activeTabId: string) => {
    writePerWindow(STORAGE_KEYS.ACTIVE_TAB, activeTabId)
}

/** Load active tab ID (this window's, seeded from the last-saved state on a fresh window) */
export const loadActiveTabFromLocalStorage = (): string | null => {
    return readPerWindow(STORAGE_KEYS.ACTIVE_TAB)
}

// Re-export from split modules for backwards compatibility
export { loadFiltersFromLocalStorage, saveFiltersToLocalStorage } from './filterSerialization'
export { deriveTabsFromFilters } from './tabDerivation'
